import { useCallback, useContext, useEffect } from 'react';
import {
  WebsiteUpdate,
  WebsiteUpdaterContext,
  WebsiteUpdaterContextProps,
} from '../../../contexts/WebsiteUpdaterContext';

export interface UseWebsiteUpdaterReturn {
  saveStatus: 'none' | 'saving' | 'done';
  setSaveStatus: (status: 'none' | 'saving' | 'done') => void;
  lastSavedAt: Date | null;
  publishing: boolean;
  publish: (websiteId: string) => Promise<any>;
  discarding: boolean;
  discardChanges: (dynamicPageId: string, websiteId: string) => Promise<any>;
  createWebsiteUpdate: WebsiteUpdaterContextProps['createWebsiteUpdate'];
  updateWebsite: (wu: WebsiteUpdate) => void;
  canRevert: boolean;
  canForward: boolean;
  revertLatestUpdate: () => boolean;
  forwardRevert: () => boolean;
  clearHistory: () => void;
  applyChangesSync: (
    websiteId: string,
    development: any,
    extra?: WebsiteUpdate[],
  ) => any;
  updateWebsiteByEvent: (wu: WebsiteUpdate) => void;
}

interface UseWebsiteUpdaterProps {
  dynamicPageId: string;
  websiteId: string;
  website: any;
  sendMessageToIframe: (message: any) => void;
}

export function useWebsiteUpdater({
  dynamicPageId,
  websiteId,
  website,
  sendMessageToIframe,
}: UseWebsiteUpdaterProps): UseWebsiteUpdaterReturn {
  const {
    saveStatus,
    setSaveStatus,
    lastSavedAt,
    publishing,
    publish: publishFn,
    discarding,
    discardChanges,
    createWebsiteUpdate,
    updateWebsite,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
    clearHistory,
    applyChangesSync,
  } = useContext(WebsiteUpdaterContext);

  const updateWebsiteByEvent = useCallback(
    (wu: WebsiteUpdate) => {
      updateWebsite(wu);

      if (website) {
        sendMessageToIframe({
          eventName: 'websiteUpdated',
          website: applyChangesSync(websiteId, website.development, [wu]),
        });
      }
    },
    [applyChangesSync, sendMessageToIframe, updateWebsite, website, websiteId],
  );

  // Reset on open
  useEffect(() => {
    setSaveStatus('none');
    clearHistory();
  }, [clearHistory, setSaveStatus]);

  const publish = useCallback(
    (websiteId: string) => {
      return publishFn(dynamicPageId, websiteId);
    },
    [publishFn, dynamicPageId],
  );

  return {
    saveStatus,
    setSaveStatus,
    lastSavedAt,
    publishing,
    publish,
    discarding,
    discardChanges,
    createWebsiteUpdate,
    updateWebsite,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
    clearHistory,
    applyChangesSync,
    updateWebsiteByEvent,
  };
}
